import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInstructorAuthContext } from '../context/InstructorAuthContext';

/**
 * Hook pour vérifier les permissions d'un instructeur
 */
export const useInstructorPermissions = (
  requiredPermissions: string | string[] = [],
  redirectTo: string = '/instructor/login',
  requireAll: boolean = false
) => {
  const navigate = useNavigate();
  const { 
    isAuthenticated,
    instructor,
    permissions,
    isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  } = useInstructorAuthContext();
  const isInstructor = !!instructor;

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      navigate(redirectTo);
      return;
    }

    if (!isInstructor) {
      navigate('/instructor/unauthorized');
      return;
    }

    if (requiredPermissions.length > 0) {
      const permsArray = Array.isArray(requiredPermissions) 
        ? requiredPermissions 
        : [requiredPermissions];

      const hasRequiredPermissions = requireAll
        ? hasAllPermissions(permsArray)
        : hasAnyPermission(permsArray);

      if (!hasRequiredPermissions) {
        navigate('/instructor/unauthorized');
        return;
      }
    }
  }, [
    isAuthenticated, 
    isInstructor, 
    isLoading, 
    requiredPermissions, 
    requireAll, 
    redirectTo, 
    navigate, 
    hasAnyPermission,
    hasAllPermissions
  ]);

  return {
    isLoading,
    isAuthenticated,
    isInstructor,
    instructor,
    permissions,
    hasPermission: (permission: string) => hasPermission(permission),
    hasAnyPermission: (perms: string[]) => hasAnyPermission(perms),
    hasAllPermissions: (perms: string[]) => hasAllPermissions(perms)
  };
};

/**
 * Hook simplifié pour vérifier une permission spécifique
 */
export const useInstructorPermission = (permission: string) => {
  const { hasPermission, isLoading, isAuthenticated, instructor } = useInstructorAuthContext();
  const isInstructor = !!instructor;
  
  return {
    hasPermission: hasPermission(permission),
    isLoading,
    isAuthenticated,
    isInstructor
  };
};
